/* Pés Felizes — memória da medida do pé.
   A calculadora (assets/pf-size-finder.js) guarda o comprimento do pé em localStorage durante 30 dias. Nas páginas de produto de calçado
   com guia de tamanhos (JSON [data-pf-size-guide] em snippets/variant-main-picker.liquid) este script destaca o tamanho recomendado
   com uma carinha, em botões ou em lista, e mostra um atalho para o escolher. Volta a aplicar-se sempre que o seletor é redesenhado. */
(() => {
  if (window.PfFoot) return;

  const KEY = 'pf-foot-cm';
  const TTL = 30 * 24 * 60 * 60 * 1000;
  const DEFAULTS = { idealMin: 0.8, idealMax: 1.2, warnMin: 0.5, target: 1.0 };
  const num = (v) => (typeof v === 'number' && isFinite(v) ? v : null);
  const round1 = (n) => Math.round(n * 10) / 10;
  const fmt = (n) => n.toFixed(1).replace('.', ',');
  const norm = (s) => String(s).toLowerCase().replace(/^\s*(eu|tam\.?|tamanho)\s*/, '').replace(/\s+/g, '').replace(',', '.');

  const PfFoot = {
    get() {
      try {
        const saved = JSON.parse(localStorage.getItem(KEY) || 'null');
        if (saved && typeof saved.cm === 'number' && Date.now() - saved.at < TTL) return saved.cm;
      } catch (e) {}
      return null;
    },
    set(cm) {
      try {
        if (cm == null) localStorage.removeItem(KEY);
        else localStorage.setItem(KEY, JSON.stringify({ cm, at: Date.now() }));
      } catch (e) {}
      document.dispatchEvent(new CustomEvent('pf:foot-change', { detail: { cm } }));
    },
    recommend(guide, foot) {
      const idealMin = num(guide.imin) ?? DEFAULTS.idealMin;
      const idealMax = num(guide.imax) ?? DEFAULTS.idealMax;
      const warnMin = num(guide.wmin) ?? DEFAULTS.warnMin;
      const warnMax = num(guide.wmax) ?? idealMax;
      const target = num(guide.tg) ?? DEFAULTS.target;
      let best = null;
      for (const [label, insole] of guide.r || []) {
        if (typeof insole !== 'number') continue;
        const gap = round1(insole - foot);
        let kind = null;
        if (gap >= idealMin && gap <= idealMax) kind = 'recommended';
        else if ((gap >= warnMin && gap < idealMin) || (gap > idealMax && gap <= warnMax)) kind = 'acceptable';
        if (!kind) continue;
        const score = (kind === 'recommended' ? 0 : 10) + Math.abs(gap - target);
        if (!best || score < best.score) best = { label: String(label), insole, gap, kind, score };
      }
      return best;
    },
  };
  window.PfFoot = PfFoot;

  const SMILE =
    '<svg class="pf-smile" viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" focusable="false">' +
    '<circle cx="12" cy="12" r="11" fill="currentColor"/><circle cx="8.5" cy="10" r="1.4" fill="#fff"/><circle cx="15.5" cy="10" r="1.4" fill="#fff"/>' +
    '<path d="M7.5 14.2c1.1 1.7 2.7 2.6 4.5 2.6s3.4-.9 4.5-2.6" fill="none" stroke="#fff" stroke-width="1.8" stroke-linecap="round"/></svg>';

  const style = document.createElement('style');
  style.textContent = `
    .pf-smile { flex: none; color: var(--pf-smile, #4f7a63); }
    .variant-option__button-label.pf-rec { position: relative; box-shadow: 0 0 0 2px var(--pf-smile, #4f7a63); }
    .variant-option__button-label.pf-rec .pf-smile { position: absolute; top: -9px; right: -9px; z-index: 1; background: rgb(var(--color-background)); border-radius: 50%; }
    .pf-rec-hint { display: flex; align-items: center; flex-wrap: wrap; gap: 6px 8px; width: 100%; margin-top: 10px; font-size: .875em; line-height: 1.35; }
    .pf-rec-hint button { padding: 0; border: 0; background: none; color: inherit; font: inherit; font-weight: 600; text-decoration: underline; text-underline-offset: 3px; cursor: pointer; }
    .pf-rec-hint__muted { opacity: .7; }
  `;
  document.head.append(style);

  const apply = (picker) => {
    const raw = picker.querySelector('script[data-pf-size-guide]');
    const wrap = picker.querySelector('[data-pf-size-option]');
    if (!raw || !wrap) return;
    let guide;
    try { guide = JSON.parse(raw.textContent); } catch (e) { return; }
    const foot = PfFoot.get();
    const rec = foot ? PfFoot.recommend(guide, foot) : null;
    const current = wrap.querySelector('select')?.value ?? wrap.querySelector('input[type="radio"]:checked')?.value ?? '';
    const sig = rec ? foot + '|' + rec.label + '|' + current : 'none';
    if (wrap.dataset.pfApplied === sig && (!rec || wrap.querySelector('.pf-rec-hint'))) return;
    wrap.dataset.pfApplied = sig;

    // limpa marcações anteriores
    wrap.querySelectorAll('.pf-rec').forEach((el) => el.classList.remove('pf-rec'));
    wrap.querySelectorAll('.variant-option__button-label .pf-smile, .pf-rec-hint').forEach((el) => el.remove());
    wrap.querySelectorAll('option[data-pf-label]').forEach((o) => { o.textContent = o.dataset.pfLabel; delete o.dataset.pfLabel; });
    if (!rec) return;

    const target = norm(rec.label);
    const radios = [...wrap.querySelectorAll('input[type="radio"]')];
    const select = wrap.querySelector('select');
    let match = null;
    let available = true;
    if (radios.length) {
      match = radios.find((r) => norm(r.value) === target);
      if (match) {
        const label = match.closest('label');
        label?.classList.add('pf-rec');
        label?.insertAdjacentHTML('beforeend', SMILE);
        available = match.dataset.optionAvailable !== 'false';
      }
    } else if (select) {
      match = [...select.options].find((o) => norm(o.value) === target);
      if (match) {
        match.dataset.pfLabel = match.textContent;
        match.textContent = match.textContent.trim() + ' 😊';
        available = !/indispon|esgotad|unavailable/i.test(match.dataset.pfLabel);
      }
    }
    if (!match) return;

    const selected = radios.length ? match.checked : select.value === match.value;
    const hint = document.createElement('p');
    hint.className = 'pf-rec-hint';
    hint.setAttribute('aria-live', 'polite');
    hint.insertAdjacentHTML('afterbegin', SMILE);
    const text = document.createElement('span');
    text.append(
      (rec.kind === 'recommended' ? 'Para o pé de ' + fmt(foot) + ' cm, o tamanho ideal é o ' : 'Para o pé de ' + fmt(foot) + ' cm, o tamanho mais próximo é o ') ,
      Object.assign(document.createElement('strong'), { textContent: rec.label }),
      rec.kind === 'recommended' ? '.' : ' (folga abaixo do ideal).'
    );
    hint.append(text);
    if (!available) {
      hint.append(Object.assign(document.createElement('span'), { className: 'pf-rec-hint__muted', textContent: 'Está esgotado.' }));
    } else if (!selected) {
      const btn = Object.assign(document.createElement('button'), { type: 'button', textContent: 'Escolher o ' + rec.label });
      btn.addEventListener('click', () => {
        if (radios.length) match.click();
        else {
          select.value = match.value;
          select.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });
      hint.append(btn);
    }
    wrap.append(hint);
  };

  const pickers = new WeakSet();
  const watch = (picker) => {
    if (pickers.has(picker)) return;
    pickers.add(picker);
    let queued = false;
    picker.addEventListener('change', () => requestAnimationFrame(() => apply(picker)));
    new MutationObserver(() => {
      if (queued) return;
      queued = true;
      requestAnimationFrame(() => { queued = false; apply(picker); });
    }).observe(picker, { childList: true, subtree: true });
    apply(picker);
  };
  const scan = () => document.querySelectorAll('variant-picker').forEach(watch);

  document.addEventListener('pf:foot-change', () => {
    document.querySelectorAll('[data-pf-size-option]').forEach((w) => delete w.dataset.pfApplied);
    scan();
    document.querySelectorAll('variant-picker').forEach(apply);
  });
  window.addEventListener('storage', (e) => { if (e.key === KEY) document.dispatchEvent(new CustomEvent('pf:foot-change')); });
  scan();
  // novos seletores (ex.: quick view)
  let scanQueued = false;
  new MutationObserver(() => {
    if (scanQueued) return;
    scanQueued = true;
    requestAnimationFrame(() => { scanQueued = false; scan(); });
  }).observe(document.body, { childList: true, subtree: true });
})();
